import { Component, computed, DestroyRef, EventEmitter, inject, input, Input, OnInit, output, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { TextInputComponent } from '../../../../../shared/ui/components/form/text-input/text-input.component';
import { ButtonComponent } from '../../../../../shared/ui/components/button/button.component';
import { MatRadioModule } from '@angular/material/radio';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { LoginCredentials } from '@core/auth';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { merge } from 'rxjs';

/**
 * Sign-In Form Component
 *
 * Pure presentational component for email input and submission.
 * No business logic or service injection.
 */
@Component({
  selector: 'app-sign-in-form',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TextInputComponent,
    ButtonComponent,
    MatRadioModule,
    MatInputModule,
    MatFormFieldModule],
  template: `
    <form [formGroup]="formGroup" class="sign-in-form" (ngSubmit)="onSubmit()">
        <app-text-input
          [control]="$any(formGroup.get('email'))"
          formControlName="email"
          type="email"
          label="Email"
          placeholder="Email"
          name="email"
          autocomplete="email"
        ></app-text-input>

        <app-text-input
          [control]="$any(formGroup.get('password'))"
          formControlName="password"
          type="password"
          label="Password"
          placeholder="Password"
          name="password"
        ></app-text-input>

      <p class="support-text">
        Need an account?
        <a href="mailto:support@ross-robotics.co.uk" class="support-link">Contact support</a>
      </p>

      <div class="form-actions">
        <app-button
          type="submit"
          variant="secondary"
          [loading]="isSubmitting()"
          [disabled]="isButtonDisabled()"
        >
          Next
        </app-button>
      </div>
    </form>
  `,
  styles: [`
    .sign-in-form {
      display: flex;
      flex-direction: column;
      gap: var(--aida-ref-spacing-100);
      width: 100%;
    }

    .support-text {
      margin: 0;
      font-size: var(--aida-ref-font-size-500);
      color: var(--aida-colour-text-secondary);
      font-family: var(--aida-ref-font-family-text);
    }

    .support-link {
      color: var(--aida-colour-text-secondary);
      text-decoration: none;
    }

    .support-link:hover {
      text-decoration: underline;
    }

    .form-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: var(--aida-ref-spacing-400);
    }

    @media (max-width: 600px) {
      .support-text {
        font-size: var(--aida-ref-font-size-400);
      }

      .form-actions {
        justify-content: stretch;
      }

      .form-actions app-button {
        width: 100%;
      }
    }
  `],
})
export class SignInFormComponent {

  isSubmitting = input<boolean>(false);
  formSubmit = output<LoginCredentials>();
  emailChange = output<void>();

  formGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl({value:'', disabled: false})
  })

  private formSignal = toSignal(this.formGroup.valueChanges,
    {initialValue: this.formGroup.getRawValue()})

  private formStatus = toSignal(this.formGroup.statusChanges, {
  initialValue: this.formGroup.status
});

  isButtonDisabled = computed(() => {
    if (this.isSubmitting()) return true;
    if (this.formStatus() === 'INVALID') return true;

    const form = this.formSignal();
    const email = form.email?.trim();
    const password = form.password;

    return !email || !password
  });

  
  constructor() {
    const emailText = this.formGroup.get("email");

    emailText?.valueChanges.pipe(takeUntilDestroyed())
    .subscribe(it => {
      this.onEmailChange();
    })
  }
  

  onSubmit(): void {
    this.formGroup.markAllAsTouched();
    const email = this.formGroup.controls.email?.value;
    const password = this.formGroup.controls.password.value;
    
    if (this.isSubmitting() || !email || !password) return;

    this.formSubmit.emit({"username": email, "password": password});
  }

  onEmailChange(): void {
    this.emailChange.emit();
  }
}
